import React from 'react';
import { FaChartLine, FaCloudSunRain, FaCrown, FaRocket, FaShieldAlt } from 'react-icons/fa';

/**
 * Badge visual que mostra o plano do usuário
 * 
 * @param {Object} props
 * @param {string} props.plano - Tipo do plano (starter, premium, pro)
 * @param {string} props.size - Tamanho (small, medium, large)
 * @param {boolean} props.showIcon - Se deve mostrar ícone
 * @param {string} props.style - Estilos customizados
 * 
 * @example
 * <PlanBadge plano="premium" size="medium" showIcon />
 */
export default function PlanBadge({ plano = 'starter', size = 'medium', showIcon = true, style = {} }) {
  
  const configs = {
    sensor_free: {
      texto: 'Gratuito Sensor',
      cor: '#0f9f8f',
      background: '#dff8f3',
      icon: FaCloudSunRain,
      textColor: '#08766b'
    },
    starter: {
      texto: 'Starter Trial',
      cor: '#159f80',
      background: '#ddf8f0',
      icon: FaRocket,
      textColor: '#087b67'
    },
    premium: {
      texto: 'Premium',
      cor: '#FFD700',
      background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
      icon: FaCrown,
      textColor: '#000'
    },
    pro: {
      texto: 'Pro',
      cor: '#9C27B0',
      background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
      icon: FaRocket,
      textColor: '#fff',
      premium: true
    },
    essential: {
      texto: 'Essencial', cor: '#0787b8', background: '#dff4fb',
      icon: FaChartLine, textColor: '#075b78'
    },
    professional: {
      texto: 'Profissional', cor: '#176dcc',
      background: 'linear-gradient(135deg, #176dcc 0%, #124f98 100%)',
      icon: FaRocket, textColor: '#fff', premium: true
    },
    management: {
      texto: 'Gestão', cor: '#07977c',
      background: 'linear-gradient(135deg, #07977c 0%, #066653 100%)',
      icon: FaShieldAlt, textColor: '#fff', premium: true
    },
    enterprise: {
      texto: 'Enterprise',
      cor: '#1A237E',
      background: 'linear-gradient(135deg, #1A237E 0%, #0D47A1 100%)',
      icon: FaCrown,
      textColor: '#fff',
      premium: true
    }
  };

  const config = configs[plano] || configs.starter;

  const sizes = {
    small: {
      padding: '4px 10px',
      fontSize: 11,
      iconSize: 10,
      borderRadius: 8
    },
    medium: {
      padding: '6px 14px',
      fontSize: 13,
      iconSize: 12,
      borderRadius: 10
    },
    large: {
      padding: '8px 18px',
      fontSize: 15,
      iconSize: 14,
      borderRadius: 12
    }
  };

  const sizeConfig = sizes[size] || sizes.medium;

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: sizeConfig.padding,
    background: config.background,
    color: config.textColor,
    borderRadius: sizeConfig.borderRadius,
    fontSize: sizeConfig.fontSize,
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    boxShadow: config.premium ? '0 4px 12px rgba(156, 39, 176, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
    whiteSpace: 'nowrap',
    ...style
  };

  const Icon = config.icon;

  return (
    <span style={badgeStyle}>
      {showIcon && Icon && <Icon size={sizeConfig.iconSize} />}
      {config.texto}
    </span>
  );
}

/**
 * Variante inline que mostra apenas ícone com tooltip
 */
export function PlanBadgeIcon({ plano = 'starter', size = 16 }) {
  const configs = {
    sensor_free: { icon: FaCloudSunRain, color: '#0f9f8f', tooltip: 'Plano Gratuito Sensor' },
    starter: { icon: FaRocket, color: '#159f80', tooltip: 'Starter Trial de 30 dias' },
    premium: { icon: FaCrown, color: '#FFD700', tooltip: 'Plano Premium legado' },
    pro: { icon: FaRocket, color: '#9C27B0', tooltip: 'Plano Pro legado' },
    essential: { icon: FaChartLine, color: '#0787b8', tooltip: 'Plano Essencial' },
    professional: { icon: FaRocket, color: '#176dcc', tooltip: 'Plano Profissional' },
    management: { icon: FaShieldAlt, color: '#07977c', tooltip: 'Plano Gestão' },
    enterprise: { icon: FaCrown, color: '#1A237E', tooltip: 'Plano Enterprise' }
  };

  const config = configs[plano] || configs.starter;
  const Icon = config.icon;

  if (!Icon) return null;

  return (
    <span title={config.tooltip} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Icon size={size} color={config.color} />
    </span>
  );
}

/**
 * Badge com informação de expiração
 */
export function PlanBadgeWithExpiry({ plano, dataExpiracao, size = 'medium', showIcon = true }) {
  const diasRestantes = dataExpiracao ? Math.ceil((new Date(dataExpiracao) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  
  const expiryStyle = {
    marginLeft: 8,
    fontSize: size === 'small' ? 10 : 11,
    opacity: 0.9,
    fontWeight: 600
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <PlanBadge plano={plano} size={size} showIcon={showIcon} />
      {diasRestantes !== null && diasRestantes > 0 && (
        <span style={expiryStyle}>
          {diasRestantes === 1 ? '(expira amanhã)' : `(${diasRestantes} dias)`}
        </span>
      )}
      {diasRestantes !== null && diasRestantes <= 0 && (
        <span style={{ ...expiryStyle, color: '#f44336', fontWeight: 700 }}>
          (EXPIRADO)
        </span>
      )}
    </div>
  );
}
